import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { setLoading } from 'Actions/loading';
import { AlertDialog } from 'Shared/AlertDialog';
import http from 'Utils/http';
import { process } from 'Utils/errorHandler';
import Logo_PNG from 'Assets/img/Infintegration_Logo_Primary.png';
import FolderTreeItem from 'Shared/flows/folders/FolderTreeItem';
import FoundCount from 'Shared/flows/folders/FoundCount';
import { getRootFolders, buildChildrenByParent, getFolderChain, getDragData, hasDragData } from 'Shared/flows/folders/folderUtils';

const Tab = ({ label, active, onClick, onFlowDrop }) => {
    const [over, setOver] = useState(false);
    const dropProps = onFlowDrop ? {
        onDragOver: (e) => { if (hasDragData(e)) { e.preventDefault(); setOver(true); } },
        onDragLeave: () => setOver(false),
        onDrop: (e) => {
            e.preventDefault();
            setOver(false);
            const data = getDragData(e);
            if (data?.kind === 'flow') {
                onFlowDrop(data.id);
            }
        },
    } : {};

    return (
        <Button
            fullWidth
            variant={active ? 'contained' : 'outlined'}
            onClick={onClick}
            {...dropProps}
            sx={{
                fontSize: '16px',
                padding: '12px 16px',
                ...(over ? { outline: '2px dashed #197AC0', outlineOffset: '2px' } : {}),
            }}
        >
            {label}
        </Button>
    );
};

const HomeRow = ({ active, count, onClick, onDropFolder, onDropFlow }) => {
    const [over, setOver] = useState(false);

    return (
        <Box
            onClick={onClick}
            onDragOver={(e) => { if (hasDragData(e)) { e.preventDefault(); setOver(true); } }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                const data = getDragData(e);
                if (data?.kind === 'folder') {
                    onDropFolder(data.id);
                } else if (data?.kind === 'flow') {
                    onDropFlow(data.id);
                }
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                paddingY: '6px',
                paddingX: '8px',
                borderRadius: '6px',
                cursor: 'pointer',
                color: active ? '#289BEF' : '#3B465C',
                backgroundColor: over ? '#d3ebfb' : (active ? '#e8f4fd' : 'transparent'),
                outline: over ? '2px dashed #289BEF' : 'none',
                '&:hover': { backgroundColor: over ? '#d3ebfb' : (active ? '#e8f4fd' : '#eef1f4') },
            }}
        >
            <HomeOutlinedIcon fontSize="small" sx={{ color: 'inherit' }} />
            <Typography sx={{ flex: 1, fontSize: '14px', color: 'inherit', fontWeight: active ? 600 : 400 }}>Home</Typography>
            <FoundCount count={count} />
        </Box>
    );
};

const FoldersSidebar = ({
    folders,
    view,
    selectedFolderId,
    onSelectView,
    onSelectFolder,
    onFoldersChanged,
    onFlowChanged,
    onCreateChild,
    onRename,
    foundCounts,
}) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [deleteTarget, setDeleteTarget] = useState(null);

    const childrenByParent = buildChildrenByParent(folders);
    const rootFolders = getRootFolders(folders);
    const expandedIds = getFolderChain(folders, selectedFolderId).slice(0, -1).map((folder) => folder.id);

    const moveFolder = (folderId, newParentId) => {
        dispatch(setLoading(true));
        http.post(`/folder/${folderId}/update`, { parent_folder_id: newParentId })
            .then(() => {
                enqueueSnackbar('Folder moved', { action: CustomSnackbar, variant: 'success' });
                onFoldersChanged();
            })
            .catch((err) => process(err, enqueueSnackbar))
            .finally(() => dispatch(setLoading(false)));
    };

    const assignFlow = (flowId, folderId) => {
        dispatch(setLoading(true));
        http.post(`/flow/${flowId}/folder`, { folder_id: folderId })
            .then(() => {
                enqueueSnackbar(folderId ? 'Flow moved to folder' : 'Flow removed from folder', { action: CustomSnackbar, variant: 'success' });
                onFoldersChanged();
                if (onFlowChanged) {
                    onFlowChanged();
                }
            })
            .catch((err) => process(err, enqueueSnackbar))
            .finally(() => dispatch(setLoading(false)));
    };

    const confirmDelete = () => {
        const folder = deleteTarget;
        setDeleteTarget(null);
        dispatch(setLoading(true));
        http.delete(`/folder/${folder.id}/delete`)
            .then(() => {
                enqueueSnackbar('Folder deleted', { action: CustomSnackbar, variant: 'success' });
                if (String(selectedFolderId) === String(folder.id)) {
                    onSelectView('all');
                }
                onFoldersChanged();
            })
            .catch((err) => process(err, enqueueSnackbar))
            .finally(() => dispatch(setLoading(false)));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ padding: '12px 12px 0', textAlign: 'center' }}>
                <img style={{ width: '220px', maxWidth: '100%' }} src={Logo_PNG} alt="" />
            </Box>

            <Box sx={{ display: 'flex', gap: '12px', marginTop: '64px' }}>
                <Tab
                    label="All Flows"
                    active={view === 'all'}
                    onClick={() => onSelectView('all')}
                    onFlowDrop={(flowId) => assignFlow(flowId, null)}
                />
                <Tab
                    label="Folders"
                    active={view === 'folders'}
                    onClick={() => onSelectView('folders')}
                />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1px',
                    marginTop: '16px',
                    overflowY: 'auto',
                    flex: 1,
                    '& > *': { flexShrink: 0 },
                }}
            >
                <HomeRow
                    active={view === 'all'}
                    count={foundCounts?.home}
                    onClick={() => onSelectView('all')}
                    onDropFolder={(folderId) => moveFolder(folderId, null)}
                    onDropFlow={(flowId) => assignFlow(flowId, null)}
                />
                {rootFolders.map((folder) => (
                    <FolderTreeItem
                        key={folder.id}
                        folder={folder}
                        childrenByParent={childrenByParent}
                        expandedIds={expandedIds}
                        level={1}
                        selectedFolder={selectedFolderId}
                        foundCounts={foundCounts}
                        onSelect={onSelectFolder}
                        onCreateChild={onCreateChild}
                        onRename={onRename}
                        onDelete={setDeleteTarget}
                        onMoveFolder={moveFolder}
                        onAssignFlow={assignFlow}
                    />
                ))}
                {!rootFolders.length && (
                    <Typography sx={{ fontSize: '13px', color: '#99a1ad', paddingLeft: '32px', paddingY: '8px' }}>
                        No folders yet
                    </Typography>
                )}
            </Box>

            <AlertDialog
                visible={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onAgree={confirmDelete}
                title={deleteTarget ? `Delete "${deleteTarget.name}"?` : ''}
                text="Subfolders will move up to the parent level. Flows will be moved to All Flows."
            />
        </Box>
    );
};

export default FoldersSidebar;
